/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { environment } from '@environments/environment';
import { map } from 'rxjs/operators';
import { ApiResParserService } from './../api-res-parser/api-res-parser.service';
import { Options } from './../../models/options';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  ChannelDTO,
  ChecklistDTO,
  SurveyUpdateDTO,
  PositionDTO,
  WizardDTO,
  SurveyDTO,
  FormDTO,
  DomainListDTO,
  IdentificationLinkDTO,
  FormUpdateDTO,
  LabelListDTO,
  UserDataDTO,
  WizardStepNextOutputDTO,
  ReportRequestDTO,
  AuthenticateDTO,
  IdentificationVerifyDTO,
  HawkScanDTO,
  DigitalSignLinkDTO,
  NoteDTO,
  LogRequestDTO
} from '../../models';

@Injectable()
export class ApiService {
  protected baseUrl = environment.apiBaseUrl;

  protected securityUrl = environment.securityBaseUrl;

  constructor(
    protected http: HttpClient,
    protected parser: ApiResParserService
  ) {}

  putLog(body: LogRequestDTO, options?: Options): Observable<any> {
    const url = `${this.baseUrl}/v1/user/action`;

    return this.http.put<any>(url, body, options);
  }

  getLabels(options?: Options): Observable<LabelListDTO> {
    const url = `${this.baseUrl}/v1/labels`;
    return this.http.get<LabelListDTO>(url, options);
  }

  getDomain(code: string, options?: Options): Observable<DomainListDTO> {
    const url = `${this.baseUrl}/v1/domains/${code}`;
    return this.http.get<DomainListDTO>(url, options);
  }

  postSecurityLogon(req: AuthenticateDTO, options?: Options): Observable<any> {
    return this.http.post<void>(
      `${this.securityUrl}/authenticate`,
      req,
      options
    );
  }

  getSecurityVerify(options?: Options): Observable<void> {
    return this.http.get<void>(`${this.securityUrl}/verify`, options);
  }

  getSecurityUserData(options?: Options): Observable<UserDataDTO> {
    return this.http
      .get<UserDataDTO>(`${this.securityUrl}/userdata`, options)
      .pipe(map(res => this.parser.userData(res)));
  }

  getPosition(positionId: string, options?: Options): Observable<PositionDTO> {
    const url = `${this.baseUrl}/v1/positions/${positionId}`;

    return this.http
      .get<PositionDTO>(url, options)
      .pipe(map(res => this.parser.position(res)));
  }

  getPositionFull(
    positionId: string,
    options?: Options
  ): Observable<PositionDTO> {
    const url = `${this.baseUrl}/v1/positions/full/${positionId}`;

    return this.http
      .get<PositionDTO>(url, options)
      .pipe(map(res => this.parser.position(res)));
  }

  postPositionWithdraw(
    positionId: string,
    body: { reasonCode: string }
  ): Observable<any> {
    const url = `${this.baseUrl}/v1/positions/${positionId}/WITHDRAW_REQUEST`;

    return this.http.post<any>(url, body);
  }

  getWizard(
    positionId: string,
    wizardCode: string,
    options?: Options
  ): Observable<WizardDTO> {
    const url = `${this.baseUrl}/v1/positions/${positionId}/wizards/${wizardCode}`;

    return this.http
      .get<WizardDTO>(url, options)
      .pipe(map(res => this.parser.wizard(res)));
  }

  putWizardStepNext(
    positionId: string,
    wizardCode: string,
    stepCode: string,
    options?: Options
  ): Observable<WizardStepNextOutputDTO> {
    const url = `${this.baseUrl}/v1/positions/${positionId}/wizards/${wizardCode}/steps/${stepCode}/next`;

    return this.http.put<WizardStepNextOutputDTO>(url, {}, options);
  }

  getSurvey(
    positionId: string,
    surveyCode: string,
    options?: Options
  ): Observable<SurveyDTO[]> {
    const url = `${this.baseUrl}/v1/positions/${positionId}/surveys/${surveyCode}`;

    return this.http
      .get<SurveyDTO[]>(url, options)
      .pipe(map(res => this.parser.surveys(res)));
  }

  putSurvey(
    positionId: string,
    surveyCode: string,
    body: SurveyUpdateDTO,
    options?: Options
  ) {
    const url = `${this.baseUrl}/v1/positions/${positionId}/surveys/${surveyCode}`;

    return this.http.put<any>(url, body, options);
  }

  getForm(
    positionId: string,
    formCode: string,
    options?: Options
  ): Observable<FormDTO[]> {
    const url = `${this.baseUrl}/v1/positions/${positionId}/forms/${formCode}`;

    return this.http
      .get<FormDTO[]>(url, options)
      .pipe(map(res => this.parser.forms(res)));
  }

  postForm(
    positionId: string,
    formCode: string,
    body: FormUpdateDTO,
    options?: Options
  ): Observable<any> {
    const url = `${this.baseUrl}/v1/positions/${positionId}/forms/${formCode}`;

    return this.http.post<any>(url, body, options);
  }

  getChecklist(
    positionId: string,
    options?: Options
  ): Observable<ChecklistDTO> {
    const url = `${this.baseUrl}/v1/positions/${positionId}/checklists/current`;

    return this.http
      .get<ChecklistDTO>(url, options)
      .pipe(map(res => this.parser.checklist(res)));
  }

  getChecklistDocumentDownload(
    positionId: string,
    docId: string,
    options?: Options
  ): Observable<any> {
    const url = `${this.baseUrl}/v1/positions/${positionId}/checklists/current/documents/${docId}/download`;

    return this.http.get(url, options);
  }

  postChecklistDocumentUpload(
    positionId: string,
    docId: string,
    body: FormData,
    options?: Options
  ): Observable<number> {
    const url = `${this.baseUrl}/v1/positions/${positionId}/checklists/current/documents/${docId}/file`;

    return this.http.post<number>(url, body, options);
  }

  putChecklistDocumentOverride(
    positionId: string,
    docId: string,
    fileId: string,
    body: FormData,
    options?: Options
  ): Observable<number> {
    const url = `${this.baseUrl}/v1/positions/${positionId}/checklists/current/documents/${docId}/file/${fileId}`;

    return this.http.put<number>(url, body, options);
  }

  getChecklistDocumentScan(
    positionId: string,
    docId: string,
    options?: Options
  ): Observable<HawkScanDTO> {
    const url = `${this.baseUrl}/v1/positions/${positionId}/checklists/current/documents/${docId}/scan
    `;

    return this.http.get<HawkScanDTO>(url, options);
  }

  postReport(
    positionId: string,
    reportCode: string,
    body: ReportRequestDTO,
    options?: Options
  ): Observable<any> {
    const url = `${this.baseUrl}/v1/positions/${positionId}/reports/${reportCode}`;

    return this.http.post<any>(url, body, options);
  }

  postIdentificationStart(
    method: string,
    body: {
      businessId: string;
      ndg: string;
    },
    options?: Options
  ): Observable<IdentificationLinkDTO> {
    const url = `${this.baseUrl}/v1/identifications/${method}/start`;

    return this.http.post<IdentificationLinkDTO>(url, body, options);
  }

  getIdentificationVerify(
    method: string,
    options?: Options
  ): Observable<IdentificationVerifyDTO> {
    const url = `${this.baseUrl}/v1/identifications/${method}/verify`;

    return this.http.get<IdentificationVerifyDTO>(url, options);
  }

  getDigitalSignatureRequestDsp(
    positionId: string,
    options?: Options
  ): Observable<DigitalSignLinkDTO> {
    const url = `${this.baseUrl}/v1/positions/${positionId}/digital-signature/request-dsp`;

    return this.http.get<DigitalSignLinkDTO>(url, options);
  }

  putObligationConfirm(
    positionId: string,
    obligationId: string,
    body: { isConfirmed: boolean },
    options?: Options
  ): Observable<any> {
    const url = `${this.baseUrl}/v1/obligation/${positionId}/confirm/${obligationId}`;

    return this.http.put<any>(url, body, options);
  }

  getNotes(
    positionId: string,
    type: string,
    options?: Options
  ): Observable<NoteDTO[]> {
    const url = `${this.baseUrl}/v1/note/${positionId}/${type}`;

    return this.http
      .get<NoteDTO[]>(url, options)
      .pipe(map(res => this.parser.notes(res)));
  }

  getChannel(positionId: string, options?: Options): Observable<ChannelDTO> {
    const url = `${this.baseUrl}/v1/chains/${positionId}`;

    return this.http
      .get<ChannelDTO>(url, options)
      .pipe(map(res => this.parser.channel(res)));
  }
}
